'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { getImageQueryParams, getMediaCondition, getSourceSrcset } from '@/app/components/media/image/utils';
import { SPLASH_ROOT_ATTRIBUTE } from '@/app/components/splash/constants';
import { getSplashImageSources } from '@/app/components/splash/image-sources';
import { SplashModal } from '@/app/components/splash/modal';
import { SPLASH_DATA_GA, SPLASH_DATA_TEST } from '@/app/components/splash/tracking';
import type { SplashControllerProps } from '@/app/components/splash/types';
import { isSplashActiveNow, readSplashAccepted } from '@/app/components/splash/utils';
import { splashVisibility } from '@/app/components/splash/visibility';
import { useGtmReady } from '@/app/hooks/useGtmReady';
import { isBrowser } from '@/app/utils/browser';
import { buildSplashAcceptKey, getSplashId } from '@/services/splash/constants';
import type { SplashPopup } from '@/services/splash/types';
import { trackDynamicEvents } from '@/tag-manager';
declare global {
  interface Window {
    __TIS_SPLASH_SHOULD_LOAD__?: boolean;
  }
}
const toScriptValue = (value: unknown): string => JSON.stringify(value).replace(/</g, '\\u003c');
const buildSplashPreloadSources = (splash: SplashPopup) => getSplashImageSources(splash).map(source => ({
  srcSet: getSourceSrcset(source),
  media: getMediaCondition(source),
  device: source.media?.device
}));
const buildSplashFallbackSrc = (splash: SplashPopup) => getImageQueryParams(splash.imageDesktop.url, {}, 'sources');
const buildSplashPrehideScript = (splash: SplashPopup): {
  __html: string;
} => {
  const cookieName = toScriptValue(buildSplashAcceptKey(splash));
  const beginDate = toScriptValue(splash.beginDate);
  const endDate = toScriptValue(splash.endDate);
  const preloadSources = toScriptValue(buildSplashPreloadSources(splash));
  const fallbackSrc = toScriptValue(buildSplashFallbackSrc(splash));
  return {
    __html: `(function(){try{
      var cookieName=${cookieName};
      var accepted=document.cookie.split(';').some(function(c){return c.trim()===cookieName+'=true';});
      var startMs=Date.parse(${beginDate});
      var endMs=Date.parse(${endDate});
      var nowMs=Date.now();
      var inactive=isNaN(startMs)||isNaN(endMs)||startMs>nowMs||endMs<=nowMs;
      if(!accepted&&!inactive){
        var sources=${preloadSources};
        window.__TIS_SPLASH_SHOULD_LOAD__=true;
        window.__TIS_SPLASH_PRELOAD_SOURCES__=sources;
        window.__TIS_SPLASH_FALLBACK_SRC__=${fallbackSrc};
        sources.forEach(function(s){
          var link=document.createElement('link');
          link.rel='preload';
          link.as='image';
          link.fetchPriority='high';
          if(s.media){link.media=s.media;}
          link.imageSrcset=s.srcSet;
          document.head.appendChild(link);
        });
        return;
      }
      var style=document.createElement('style');
      style.textContent='[${SPLASH_ROOT_ATTRIBUTE}="true"]{display:none!important}';
      document.head.appendChild(style);
    }catch(e){}})();`
  };
};
const buildSplashImageLoadScript = (): string => `
(function(){try{
  var sources=window.__TIS_SPLASH_PRELOAD_SOURCES__;
  var fallback=window.__TIS_SPLASH_FALLBACK_SRC__;
  if(!window.__TIS_SPLASH_SHOULD_LOAD__||!Array.isArray(sources)||!fallback){
    return;
  }
  var root=document.querySelector('[${SPLASH_ROOT_ATTRIBUTE}="true"]');
  var picture=root&&root.querySelector('[data-test="${SPLASH_DATA_TEST.image}"]');
  var img=picture&&picture.querySelector('img');
  if(!picture||!img){return;}

  var existingSources=picture.querySelectorAll('source');
  for(var i=0;i<existingSources.length;i++){existingSources[i].remove();}
  sources.forEach(function(s){
    var sourceEl=document.createElement('source');
    sourceEl.setAttribute('srcset',s.srcSet);
    if(s.media){sourceEl.setAttribute('media',s.media);}
    picture.insertBefore(sourceEl,img);
  });

  img.setAttribute('src',fallback);
}catch(e){}})();
`;
const subscribeToWindowEvents = (onStoreChange: () => void) => {
  window.addEventListener('focus', onStoreChange);
  window.addEventListener('storage', onStoreChange);
  document.addEventListener('visibilitychange', onStoreChange);
  return () => {
    window.removeEventListener('focus', onStoreChange);
    window.removeEventListener('storage', onStoreChange);
    document.removeEventListener('visibilitychange', onStoreChange);
  };
};
const getServerSplashVisibilitySnapshot = () => true;
const subscribeSplashImageReady = () => () => undefined;
const getClientSplashImageReadySnapshot = () => true;
const getServerSplashImageReadySnapshot = () => isBrowser && Boolean(window.__TIS_SPLASH_SHOULD_LOAD__);
export const SplashController = t0 => {
  const $ = _c(28);
  const {
    splash,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== splash) {
    t2 = getSplashId(splash);
    $[0] = splash;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const splashId = t2;
  const [dismissedId, setDismissedId] = useState(null);
  const [isGtmReady, setIsGtmReady] = useState(false);
  const isDismissed = dismissedId === splashId;
  let t3;
  if ($[2] !== enabled || $[3] !== splash) {
    t3 = () => {
      if (!splash || !enabled) {
        return false;
      }
      const isActive = isSplashActiveNow(splash);
      const isAccepted = readSplashAccepted(splash);
      return isActive && !isAccepted;
    };
    $[2] = enabled;
    $[3] = splash;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const getClientSplashVisibilitySnapshot = t3;
  const isClientVisible = useSyncExternalStore(subscribeToWindowEvents, getClientSplashVisibilitySnapshot, getServerSplashVisibilitySnapshot);
  const loadImageImmediately = useSyncExternalStore(subscribeSplashImageReady, getClientSplashImageReadySnapshot, getServerSplashImageReadySnapshot);
  let t4;
  if ($[5] !== splashId) {
    t4 = () => setDismissedId(splashId);
    $[5] = splashId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleClose = t4;
  const canShow = !!splashId && enabled && isClientVisible && !isDismissed;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsGtmReady(true);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useGtmReady(t5);
  let t6;
  let t7;
  if ($[8] !== enabled || $[9] !== isGtmReady || $[10] !== splash) {
    t6 = () => {
      if (!isGtmReady) {
        return;
      }
      if (!splash || !enabled) {
        return;
      }
      if (!isSplashActiveNow(splash)) {
        return;
      }
      if (readSplashAccepted(splash)) {
        trackDynamicEvents({
          event_name: SPLASH_DATA_GA.SKIPPED,
          group_name: "splash"
        });
      } else {
        trackDynamicEvents({
          event_name: SPLASH_DATA_GA.VIEW,
          group_name: "splash"
        });
      }
    };
    t7 = [splash, enabled, isGtmReady];
    $[8] = enabled;
    $[9] = isGtmReady;
    $[10] = splash;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[13] !== canShow) {
    t8 = () => {
      splashVisibility.set(canShow);
      return _temp;
    };
    t9 = [canShow];
    $[13] = canShow;
    $[14] = t8;
    $[15] = t9;
  } else {
    t8 = $[14];
    t9 = $[15];
  }
  useEffect(t8, t9);
  if (!splash || !canShow) {
    return null;
  }
  let t10;
  if ($[16] !== splash) {
    t10 = buildSplashPrehideScript(splash);
    $[16] = splash;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <script dangerouslySetInnerHTML={t10} />;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== handleClose || $[21] !== loadImageImmediately || $[22] !== splash) {
    t12 = <SplashModal splash={splash} loadImageImmediately={loadImageImmediately} onClose={handleClose} />;
    $[20] = handleClose;
    $[21] = loadImageImmediately;
    $[22] = splash;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <script dangerouslySetInnerHTML={{
      __html: buildSplashImageLoadScript()
    }} />;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t12) {
    t14 = <>{t11}{t12}{t13}</>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  return t14;
};
function _temp() {
  return splashVisibility.set(false);
}